import type { EstimateDetail, Fuel, Grade7, Origin, ServiceHistory, Severity, Usage } from 'used-vehicle-contracts';
import { fromTriState, toOptionalNumber, toTriState } from './estimates-display';
import type { EstimateDraft, InspectionDraft } from './valuation-local';

/**
 * The estimator's own copy of the record.
 *
 * Every field is a **string**, because that is what an `<input>` holds: an odometer reading that is
 * being retyped passes through `"4"` and `"42"` on its way to `"42000"`, and a number-typed state would
 * either reject those or silently price the vehicle as if it had covered four kilometres. The
 * conversion to numbers, nulls and tri-states happens in one place — here — on its way to the engine
 * and on its way to the server.
 */
export interface EstimatorForm {
  customerName: string;
  customerPhone: string;
  customerAskingPrice: string;
  make: string;
  model: string;
  trim: string;
  modelYear: string;
  odometerKm: string;
  vin: string;
  ownerCount: string;
  fuel: Fuel;
  origin: Origin | '';
  usage: Usage | '';
  serviceHistory: ServiceHistory;
  registrationExpiry: string;
  /** `''` = nobody has answered, which is never the same as "No" (PP §7.1). */
  faceliftApplies: string;
  customsApplies: string;
  /** The last price for the vehicle and trim, as typed on this estimate (plan §11.19). */
  referencePrice: string;
  /** Registration and transfer fees typed for this estimate; `''` = use the template. */
  registrationTransferFee: string;
  grade7: Grade7;
  interior: Severity;
  engine: Severity;
  hybrid: Severity;
  tires: Severity;
  damageGroup1Index: string;
  damageGroup2Index: string;
  inspectorNotes: string;
}

const text = (value: string | null | undefined): string => value ?? '';

const num = (value: number | null | undefined): string => (value === null || value === undefined ? '' : String(value));

export const formFromDetail = (detail: EstimateDetail): EstimatorForm => ({
  customerName: text(detail.customer?.name),
  customerPhone: text(detail.customer?.phone),
  customerAskingPrice: num(detail.customerAskingPrice),
  make: text(detail.make),
  model: text(detail.model),
  trim: text(detail.trim),
  modelYear: num(detail.modelYear),
  odometerKm: num(detail.odometerKm),
  vin: text(detail.vin),
  ownerCount: num(detail.ownerCount),
  fuel: detail.fuel,
  origin: detail.origin ?? '',
  usage: detail.usage ?? '',
  serviceHistory: detail.serviceHistory,
  registrationExpiry: text(detail.registrationExpiry),
  faceliftApplies: fromTriState(detail.faceliftApplies),
  customsApplies: fromTriState(detail.customsApplies),
  referencePrice: num(detail.referencePrice),
  registrationTransferFee: num(detail.registrationTransferFee),
  grade7: detail.inspection?.grade7 ?? 'UNKNOWN',
  interior: detail.inspection?.interior ?? 'UNKNOWN',
  engine: detail.inspection?.engine ?? 'UNKNOWN',
  hybrid: detail.inspection?.hybrid ?? 'UNKNOWN',
  tires: detail.inspection?.tires ?? 'UNKNOWN',
  damageGroup1Index: num(detail.inspection?.damageGroup1Index),
  damageGroup2Index: num(detail.inspection?.damageGroup2Index),
  inspectorNotes: text(detail.inspection?.inspectorNotes),
});

/**
 * The facts the engine reads. The profit mode and the cost estimates are **not** on the form — they come
 * from the template and from step 3 — so they are taken from the saved record. The reference price and
 * the typed registration fee are on step 1 since the 2026-09-23 review, so they price as they are typed.
 */
export const toFactsDraft = (form: EstimatorForm, detail: EstimateDetail): EstimateDraft => ({
  modelYear: toOptionalNumber(form.modelYear),
  referencePrice: toOptionalNumber(form.referencePrice),
  registrationTransferFee: toOptionalNumber(form.registrationTransferFee),
  odometerKm: toOptionalNumber(form.odometerKm),
  ownerCount: toOptionalNumber(form.ownerCount),
  fuel: form.fuel,
  registrationExpiry: form.registrationExpiry === '' ? null : form.registrationExpiry,
  faceliftApplies: toTriState(form.faceliftApplies),
  customsApplies: toTriState(form.customsApplies),
  hasSpareKey: detail.hasSpareKey,
  hasOwnersManual: detail.hasOwnersManual,
  hasToolKit: detail.hasToolKit,
  hasSpareTire: detail.hasSpareTire,
  acquisitionType: detail.acquisitionType,
  profitMode: detail.profitMode,
  taxTrafficEstimate: detail.taxTrafficEstimate,
  otherDirectCostEstimate: detail.otherDirectCostEstimate,
  ctaOffer: detail.ctaOffer,
  valuationDate: detail.valuationDate,
});

export const toInspectionDraft = (form: EstimatorForm): InspectionDraft => ({
  grade7: form.grade7,
  interior: form.interior,
  engine: form.engine,
  hybrid: form.hybrid,
  tires: form.tires,
  damageGroup1Index: toOptionalNumber(form.damageGroup1Index),
  damageGroup2Index: toOptionalNumber(form.damageGroup2Index),
});

/**
 * The vehicle identity step 1 cannot be left without (business review 2026-09-23, C6), in the order the
 * fields appear. Autosave is unaffected: a half-filled record is still saved as it is typed; only
 * "Continue" waits for these.
 */
export const REQUIRED_IDENTITY_FIELDS = ['templateId', 'make', 'model', 'trim', 'modelYear', 'odometerKm', 'vin', 'ownerCount'] as const;

export type RequiredIdentityField = (typeof REQUIRED_IDENTITY_FIELDS)[number];

export const missingIdentityFields = (form: EstimatorForm, detail: Pick<EstimateDetail, 'templateId'>): RequiredIdentityField[] =>
  REQUIRED_IDENTITY_FIELDS.filter((field) => {
    if (field === 'templateId') {
      return detail.templateId === null;
    }

    if (field === 'modelYear' || field === 'odometerKm' || field === 'ownerCount') {
      return toOptionalNumber(form[field]) === null;
    }

    return form[field].trim() === '';
  });
